import { addAnswer, addPrefilledAnswers, clearAnswers } from '../answers-slice'
import {
  AllSteps,
  Answer,
  AnswerId,
  Answers,
  ProviderSlugs,
  ProviderType,
  Scenario,
  ScenarioStep,
  ScenarioStepModifiableElements,
  ScenarioType,
  StepId,
  Steps,
  Translatable,
} from '../interfaces'
import { call, delay, put, select, takeEvery } from 'redux-saga/effects'
import {
  selectAnswers,
  selectCurrentStep,
  selectCurrentStepId,
  selectIsCurrentStepPrefilled,
  selectNextStepId,
  selectOriginalScenarioType,
  selectPrefilledAnswers,
  selectReferrerUrl,
  selectScenario,
  selectScenarioType,
  selectScenarioCookie,
  selectScenarioScenarioType,
  selectScenarioAdditionalInformation,
} from '../selectors'
import { gotoStep, nextStep, NextStepPayload } from '../step-slice'
import { finishScenario, setIsScenarioLoading, setIsScenarioWaitingForAnswer, setScenario } from '../scenario-slice'
import { createToast } from '../../components/toast'
import { refreshPhonesSync } from './mobile'
import { RootState } from '../../app/state'
import {
  clearScenarioCookie,
  getCookie,
  parseScenario,
  setAdbannerTargetingKeyValueCookie,
  setCurrentProvider,
  setInternetSpeedSearched,
} from '../../scenarios'
import { setCookie } from '../../scenarios'
import i18next from 'i18next'
import { updateScenario } from '../../state'
import { addressLead, getProviderTypeFromScenario } from '../../scenarios/utils'
import {
  checkProviderAvailability,
  sendAlertRequest,
  sendLeadRequest,
  sendReviewsRequest,
  sendNewTicketRequest,
  sendNewImmigrantRequest,
  sendB2BRequest,
  sendConsentNewsletterRequest,
  sendNewUserUpdatedRequest,
} from '../../connectivity/api'
import { getLocale } from '../../i18n/locale'
import { getAdditionalQueryParameters, getQueryParams } from '../../connectivity/request'
import md5 from 'blueimp-md5'

export function* answerSaga() {
  yield takeEvery(addAnswer.type, answerAdded)
}

export function* answerAdded(action: ReturnType<typeof addAnswer>): Generator<any, void, any> {
  const scenarioType: ScenarioType = yield select(selectScenarioType)
  const providerName: string | undefined = getCookie('providerName')

  yield put(setIsScenarioWaitingForAnswer(true))

  if (
    scenarioType === ScenarioType.B2B &&
    action.payload.stepId === Steps.BUSINESS_CONNECTIVITY &&
    action.payload.answerIds.includes('mobile-internet-option')
  ) {
    yield put(
      updateScenario({
        stepToUpdate: Steps.CANADA_US_COVERAGE,
        value: Steps.CURRENT_PROVIDER_INTERNET_B2B,
        element: ScenarioStepModifiableElements.NEXT,
      }),
    )
  } else if (scenarioType === ScenarioType.B2B && action.payload.stepId === Steps.BUSINESS_CONNECTIVITY) {
    yield put(
      updateScenario({
        stepToUpdate: Steps.CANADA_US_COVERAGE,
        value: Steps.FINAL_WORD_B2B,
        element: ScenarioStepModifiableElements.NEXT,
      }),
    )
  }

  if (
    scenarioType === ScenarioType.LEAD_INTERNET &&
    action.payload.stepId === Steps.PROVIDER_AVAILABILITY &&
    action.payload.answerIds.includes('continue-with-provider') &&
    action.payload.answerIds.includes('false') &&
    providerName === 'Bell' // this is only valid for bell
  ) {
    yield put(finishScenario())
  } else if (
    scenarioType === ScenarioType.LEAD_MOBILE &&
    action.payload.stepId === Steps.PROVIDER_AVAILABILITY &&
    action.payload.answerIds.includes('continue-with-provider') &&
    action.payload.answerIds.includes('false')
  ) {
    yield put(finishScenario())
  } else if (
    (scenarioType === ScenarioType.LEAD_INTERNET || scenarioType === ScenarioType.LEAD_MOBILE) &&
    action.payload.stepId === Steps.NEW_CLIENT_VERIFICATION &&
    action.payload.answerIds.includes('continue-with-provider') &&
    action.payload.answerIds.includes('false')
  ) {
    yield put(finishScenario())
  } else {
    const currentStep: ScenarioStep = yield select(selectCurrentStep)
    const currentStepId: Steps = yield select(selectCurrentStepId)
    const { answerIds, stepId, hidden } = action.payload
    let hasTemporaryNextStep = false
    let previousNextStep = null
    let isProvinceStep = false

    if (currentStep) {
      if (currentStep.clears) {
        yield put(clearAnswers({ ids: currentStep.clears }))
      }

      yield call(autoFillFutureAnswers, currentStep.answers, answerIds)

      if (currentStep.id === Steps.PROVINCE) {
        if (answerIds[0] !== 'I-dont-know') {
          setCookie(Steps.PROVINCE, answerIds[0])
          setCookie('stepQ', 'yes')
          isProvinceStep = true
        }
      }

      if (currentStep.id === Steps.BIG_ENTREPRISE || currentStep.id === Steps.BIG_ENTREPRISE2) {
        setCookie(Steps.BIG_ENTREPRISE, answerIds[0])
      }

      if (currentStep.id === Steps.CURRENT_PROVIDER) {
        setCurrentProvider({ providerSlug: answerIds[0], type: scenarioType })
      }

      if (currentStep.id === Steps.CURRENT_PROVIDER_MOBILE || currentStep.id === Steps.CURRENT_PROVIDER_MOBILE_LEAD) {
        const scenarioTypes = [
          ScenarioType.INTERNET,
          ScenarioType.MOBILE,
          ScenarioType.LEAD_INTERNET,
          ScenarioType.LEAD_MOBILE,
        ]

        if (scenarioTypes.includes(scenarioType)) {
          setCurrentProvider({ providerSlug: answerIds[0], type: ScenarioType.MOBILE })
        }
      }

      if (
        currentStep.id === Steps.CURRENT_PROVIDER_INTERNET ||
        currentStep.id === Steps.CURRENT_PROVIDER_INTERNET_LEAD
      ) {
        const scenarioTypes = [
          ScenarioType.INTERNET,
          ScenarioType.MOBILE,
          ScenarioType.LEAD_INTERNET,
          ScenarioType.LEAD_MOBILE,
        ]

        if (scenarioTypes.includes(scenarioType)) {
          setCurrentProvider({ providerSlug: answerIds[0], type: ScenarioType.INTERNET })
        }
      }

      if (
        currentStep.id === Steps.INTERNET_SPEED ||
        currentStep.id === Steps.SLIDER_DATA_AMOUNT ||
        stepId === Steps.DOWNLOAD_SPEED
      ) {
        setInternetSpeedSearched({ speed: answerIds[0] })
      }

      if (currentStep.id === Steps.BIG_ENTREPRISE || currentStep.id === Steps.BIG_ENTREPRISE2) {
        setAdbannerTargetingKeyValueCookie({ key: 'GovEmployee', value: answerIds[0] })
      }

      if (currentStep.id === Steps.ARE_YOU_MOVING) {
        setAdbannerTargetingKeyValueCookie({ key: 'moveout', value: answerIds[0] })
      }

      if (currentStep.id === Steps.INTERNET_USAGE) {
        const answers: Answers = yield select(selectAnswers)

        if (answers[currentStepId]?.includes('finishScenario')) {
          yield put(
            updateScenario({
              stepToUpdate: currentStepId,
              value: Steps.DONE,
              element: ScenarioStepModifiableElements.NEXT,
            }),
          )
        }
      }

      if (
        currentStepId === Steps.CURRENT_PROVIDER_INTERNET_LEAD ||
        currentStepId === Steps.CURRENT_PROVIDER_MOBILE_LEAD
      ) {
        const scenarioType: ScenarioType = yield select(selectScenarioType)
        const answers: Answers = yield select(selectAnswers)
        const scenarioAdditionalInformation: any = yield select(selectScenarioAdditionalInformation)
        const leadProvider = scenarioAdditionalInformation.providerSlug as string

        if (scenarioType === ScenarioType.LEAD_INTERNET) {
          if (answers[Steps.CURRENT_PROVIDER_INTERNET_LEAD] && answers[Steps.CURRENT_PROVIDER_INTERNET_LEAD]![0]) {
            yield call(
              updateScenarioStep,
              Steps.CURRENT_PROVIDER_INTERNET_LEAD,
              leadProvider,
              answers[Steps.CURRENT_PROVIDER_INTERNET_LEAD]![0],
            )
          }
        }

        if (scenarioType === ScenarioType.LEAD_MOBILE) {
          if (answers[Steps.CURRENT_PROVIDER_MOBILE_LEAD] && answers[Steps.CURRENT_PROVIDER_MOBILE_LEAD]![0]) {
            if (answers[Steps.CURRENT_PROVIDER_MOBILE_LEAD] && answers[Steps.CURRENT_PROVIDER_MOBILE_LEAD]![0]) {
              yield call(
                updateScenarioStep,
                Steps.CURRENT_PROVIDER_MOBILE_LEAD,
                leadProvider,
                answers[Steps.CURRENT_PROVIDER_MOBILE_LEAD]![0],
              )
            }
          }
        }
      }

      try {
        if (currentStepId === Steps.ADDRESS_LOCATION) {
          if (scenarioType === ScenarioType.LEAD_INTERNET) {
            const answers: Answers = yield select(selectAnswers)
            const additionalInformation: any = yield select(selectScenarioAdditionalInformation)
            const addressLocation: string | undefined = answers[Steps.ADDRESS_LOCATION]?.[0]
            const providerSlug = additionalInformation['providerSlug'] as string

            if (addressLocation && providerSlug) {
              if (addressLead.address !== addressLocation || addressLead.status !== 'sent') {
                const tail = btoa(`{"providerSlug": "${providerSlug}", "address": "${addressLocation}"}`)
                const hashString = `${providerSlug}_${new Date().toISOString().slice(0, 10)}`
                const hash = md5(hashString)

                yield call(
                  fetch,
                  `${process.env.REACT_APP_PORTAL_URL}/api/scraperSingleDiscoveryLaunch/${hash}/${tail}`,
                  {
                    method: 'GET',
                    mode: 'no-cors',
                    headers: {
                      'Content-type': 'application/json',
                    },
                  },
                )

                addressLead.address = addressLocation
                addressLead.status = 'sent'
              }
            }
          }
        }
      } catch (_error) {
        console.log('Error sending address location')
      }

      if (action.payload.stepId === Steps.PHONE_MODEL_ID) {
        previousNextStep = currentStep.next
        yield put(
          updateScenario({
            stepToUpdate: currentStepId,
            value: Steps.EMAIL_ADDRESS,
            element: ScenarioStepModifiableElements.NEXT,
          }),
        )
        hasTemporaryNextStep = true
      }

      if (action.payload.stepId === Steps.SLIDER_DATA) {
        const answers: Answers = yield select(selectAnswers)
        if (answers && answers['slider-data'] && parseInt(answers['slider-data'][0]) <= 20000) {
          previousNextStep = currentStep.next
          yield put(
            updateScenario({
              stepToUpdate: currentStepId,
              value: Steps.BUY_OR_BRING_PHONE,
              element: ScenarioStepModifiableElements.NEXT,
            }),
          )
          hasTemporaryNextStep = true
        }
      }

      if (!hidden) {
        yield call(proceedToNextStep)
      }

      if (hasTemporaryNextStep && previousNextStep) {
        yield put(
          updateScenario({
            stepToUpdate: currentStepId,
            value: previousNextStep,
            element: ScenarioStepModifiableElements.NEXT,
          }),
        )
        hasTemporaryNextStep = false
      }

      if (currentStep.id !== Steps.EMAIL_ADDRESS) {
        yield put(setIsScenarioWaitingForAnswer(false))
      } else {
        yield delay(3000) // to allow cio do its thing
        yield put(setIsScenarioWaitingForAnswer(false))
      }

      if (isProvinceStep) {
        yield call(refreshPhonesWithinCurrentScenario)
      }
    }
  }
}

function* updateScenarioStep(step: Steps, leadProvider: string, currentProvider: string) {
  const nextStep = leadProvider !== currentProvider ? Steps.ADDRESS_LOCATION : Steps.NEW_CLIENT_VERIFICATION
  yield put(
    updateScenario({
      stepToUpdate: step,
      value: nextStep,
      element: ScenarioStepModifiableElements.NEXT,
    }),
  )
}

function* refreshPhonesWithinCurrentScenario() {
  const scenarioType: ScenarioType = yield select(selectScenarioType)

  yield put(setIsScenarioLoading())
  if (
    scenarioType === ScenarioType.MOBILE ||
    scenarioType === ScenarioType.MOBILE_SEARCH_EMAIL ||
    scenarioType === ScenarioType.MOBILE_ALERT
  ) {
    yield call(refreshPhonesSync)
  }

  const state: RootState = yield select()
  const scenario: Scenario = yield select(selectScenario)
  const newScenario: Scenario = yield call(parseScenario, { scenario, state })
  yield put(setScenario(newScenario))
  yield put(setIsScenarioLoading(false))
}

function* autoFillFutureAnswers(stepAnswers: Answer[], currentAnswers: AnswerId[]) {
  for (const currentAnswer of currentAnswers) {
    const answer = stepAnswers.find((stepAnswer) => stepAnswer.id === currentAnswer)

    if (answer && answer.autoFillAnswers) {
      yield put(addPrefilledAnswers(answer.autoFillAnswers))
    }
  }
}

function* checkCurrentProvinceIsCorrect() {
  const scenarioAdditionalInformation: Record<string, unknown> = yield select(selectScenarioAdditionalInformation)
  const searchProvince = scenarioAdditionalInformation['sr_province'] as string
  const internetSearchProvince = getCookie('internetSearchProvince') || 'nc'
  return searchProvince === internetSearchProvince
}

export function* proceedToNextStep(): unknown {
  const nextStepId: StepId | null = yield select(selectNextStepId)
  const scenario: Scenario = yield select(selectScenario)
  const providerSlugsThatNeedAddressAvailabilityStep = [ProviderSlugs.FIBRESTREAM, ProviderSlugs.BELL]

  if (!nextStepId) {
    yield call(createToast, 'Unable to determine next step', 'error')
  }

  if (nextStepId === Steps.START_RATING) {
    const providerName: string | undefined = getCookie('providerNameReviews')
    const providerType: ProviderType = getProviderTypeFromScenario(scenario)

    yield put(
      updateScenario({
        stepToUpdate: Steps.START_RATING,
        value: i18next.t('start-rating_question', {
          providerName,
          providerType,
        }) as Translatable<`${AllSteps}_question`>,
        element: ScenarioStepModifiableElements.QUESTION,
      }),
    )
  }

  if (nextStepId === Steps.CONFIRM_LEAD) {
    const providerName: string | undefined = getCookie('providerName')

    yield put(
      updateScenario({
        stepToUpdate: Steps.CONFIRM_LEAD,
        value: i18next.t('confirm-lead_answerPlaceholder', {
          providerName,
        }) as Translatable<`${AllSteps}_answerPlaceholder`>,
        element: ScenarioStepModifiableElements.ANSWER_PLACEHOLDER,
      }),
    )
  }

  if (nextStepId === Steps.THANKS || nextStepId === Steps.THANKS_COUNTRY) {
    const answers: Answers = yield select(selectAnswers)
    switch (scenario.type) {
      case ScenarioType.MOBILE_REVIEWS:
      case ScenarioType.INTERNET_REVIEWS:
      case ScenarioType.MOBILE_REVIEWS_AWARD:
      case ScenarioType.INTERNET_REVIEWS_AWARD:
        yield call(sendReviews, answers)
        break
      case ScenarioType.INTERNET_ALERT:
      case ScenarioType.MOBILE_ALERT:
      case ScenarioType.INTERNET_ALERT_OBSERVER:
      case ScenarioType.MOBILE_ALERT_OBSERVER:
      case ScenarioType.COMPETITION_INTERNET_ALERT:
        yield call(sendAlert, answers)
        break
      case ScenarioType.LEAD_MOBILE:
      case ScenarioType.LEAD_INTERNET:
        yield call(sendLead, answers)
        break
      case ScenarioType.NEWSLETTER:
        yield call(sendNewsletter, answers)
        break
      case ScenarioType.TICKET:
        yield call(sendNewTicket, answers)
        break
      case ScenarioType.CANADA_VISITOR:
        yield call(sendNewImmigrantInfo, answers)
        break
      case ScenarioType.B2B:
        yield call(sendB2BInfo, answers)
        break
      case ScenarioType.COUNTRY:
        yield call(sendNewUserUpdatedInfo, answers)
        break
      default:
        console.warn('No action')
    }
  }

  if (nextStepId === Steps.PROVIDER_AVAILABILITY) {
    try {
      const providerSlug = JSON.parse(getCookie('scenario')).initialInformation?.providerSlug
      const planId = JSON.parse(getCookie('scenario')).initialInformation?.planId
      const scenarioType: ScenarioType = yield select(selectScenarioType)
      let postalCode = getCookie('internetSearchPostalcode').replace(/\s+/, '')

      if (!postalCode) {
        postalCode = 'nc'
      }
      // const availability = yield call(checkProviderAvailability, providerSlug)

      if (
        providerSlugsThatNeedAddressAvailabilityStep.includes(providerSlug) &&
        scenarioType !== ScenarioType.LEAD_MOBILE
      ) {
        const availability = yield call(checkProviderAvailability, {
          providerSlug,
          planId,
          postalCode,
        }) // Temporarily to only be used by fibrestream and bell
        yield put(
          addAnswer({
            stepId: Steps.PROVIDER_AVAILABILITY,
            answerIds: [availability],
            hidden: true,
            prefilled: false,
          }),
        )
      } else if (scenarioType === ScenarioType.LEAD_MOBILE) {
        const isCorrectProvince = yield call(checkCurrentProvinceIsCorrect)
        yield put(
          addAnswer({
            stepId: Steps.PROVIDER_AVAILABILITY,
            answerIds: [isCorrectProvince ? 'true' : 'false'],
            hidden: true,
            prefilled: false,
          }),
        )
      } else {
        // Temporarily changed to avoid slow call to api
        // yield put(
        //   addAnswer({
        //     stepId: Steps.PROVIDER_AVAILABILITY,
        //     answerIds: ['continue-with-provider', availability],
        //     hidden: true,
        //     prefilled: true,
        //   }),
        // )

        // Must use the the api when fixed proble of speed
        yield put(
          addAnswer({
            stepId: Steps.PROVIDER_AVAILABILITY,
            answerIds: ['continue-with-provider', 'false'],
            hidden: true,
            prefilled: true,
          }),
        )
      }
    } catch (error) {
      console.log('There is some error')
    }
  }

  if (nextStepId === Steps.THANKS) {
    if (scenario.type === ScenarioType.CANADA_VISITOR) {
      setCookie('visitor', '1', 7) // cookie visitor has values 1, 2, 3 to save the route of the visitor
      const locale = getLocale()
      clearScenarioCookie()
      setTimeout(() => {
        window.location.assign(`https://www.planhub.ca/${locale}/immigration/welcome-canada#landingstepq`)
      }, 2000)
    }
  }

  if (nextStepId === Steps.DONE) {
    const currentStep: ScenarioStep = yield select(selectCurrentStep)

    if (scenario.type === ScenarioType.DECISION) {
      const referrer: string = encodeURIComponent(yield select(selectReferrerUrl))
      let answers: Answers = yield select(selectAnswers)
      const { protocol, host, pathname } = window.location
      const scenarioType = answers['decision-mobile-internet'] ? answers['decision-mobile-internet']![0] : 'mobile'
      const locale = getLocale()
      answers = {}
      clearScenarioCookie()
      window.location.assign(
        `${protocol}//${host}${pathname}?locale=${locale}&scenarioType=${scenarioType}&referrer=${referrer}`,
      )
    } else if (scenario.type === ScenarioType.DECISION_ALERT) {
      const answers: Answers = yield select(selectAnswers)
      let sortByParameter = ''

      if (answers && answers['decision-alert-common-price-drop'] && answers['decision-alert-common-price-drop'][0]) {
        const referrer: string = encodeURIComponent(yield select(selectReferrerUrl))
        let originalScenarioType = getCookie('scenario') ? JSON.parse(getCookie('scenario')).type : null
        if (!originalScenarioType) {
          originalScenarioType = yield select(selectOriginalScenarioType)
        }
        // const originalScenarioType = getCookie('scenario') ? JSON.parse(getCookie('scenario')).type : originalScenario
        const scenarioType = findAlertScenarioType(answers['decision-alert-common-price-drop'][0], originalScenarioType)
        if (answers['decision-alert-common-price-drop'][0] === 'price-drop') {
          clearScenarioCookie()
          sortByParameter = '&sortBy=2years'
        } else if (answers['decision-alert-common-price-drop'][0] === 'alert-search') {
          sortByParameter = '&sortBy=best'
        }

        const { protocol, host, pathname } = window.location
        const locale = getLocale()
        const scenarioAdditionalInformation: any = yield select(selectScenarioAdditionalInformation)

        if (scenarioAdditionalInformation) {
          const emailofuser = scenarioAdditionalInformation.emailofuser as string

          if (emailofuser) {
            window.location.assign(
              `${protocol}//${host}${pathname}?locale=${locale}&scenarioType=${scenarioType}&referrer=${referrer}&decision=1&emailofuser=${emailofuser}${sortByParameter}`,
            )
          } else {
            window.location.assign(
              `${protocol}//${host}${pathname}?locale=${locale}&scenarioType=${scenarioType}&referrer=${referrer}&decision=1${sortByParameter}`,
            )
          }
        } else {
          window.location.assign(
            `${protocol}//${host}${pathname}?locale=${locale}&scenarioType=${scenarioType}&referrer=${referrer}&decision=1${sortByParameter}`,
          )
        }
      }
    } else if (
      (scenario.type === ScenarioType.MOBILE || scenario.type === ScenarioType.INTERNET) &&
      (currentStep.id === Steps.BUSINESS_OR_NOT_MOBILE || currentStep.id === Steps.BUSINESS_OR_NOT_INTERNET)
    ) {
      const { protocol, host, pathname } = window.location
      const locale = getLocale()
      const referrer: string = encodeURIComponent(yield select(selectReferrerUrl))
      window.location.assign(
        `${protocol}//${host}${pathname}?locale=${locale}&scenarioType=b2b&referrer=${referrer}&decision=1`,
      )
    } else {
      if (scenario.type === ScenarioType.MOBILE_SEARCH_EMAIL || scenario.id === 'mobile-d') {
        const answers: Answers = yield select(selectAnswers)
        if (answers && answers['email-address'] && answers['email-address'][0]) {
          yield call(sendAlert, answers)
        }
      }
      if (
        scenario.type === ScenarioType.EMAIL_PAGE_GO_INTERNET ||
        scenario.type === ScenarioType.EMAIL_PAGE_GO_MOBILE ||
        scenario.type === ScenarioType.MOBILE_REVIEWS_AWARD ||
        scenario.type === ScenarioType.INTERNET_REVIEWS_AWARD
      ) {
        const answers: Answers = yield select(selectAnswers)
        if (
          answers &&
          ((answers['confirm-newsletter'] && answers['confirm-newsletter'][0]) ||
            (answers['confirm-review'] && answers['confirm-review'].includes('confirm-newsletters')))
        ) {
          yield delay(2000)
          yield call(sendConsentNewsletterInfo, answers)
        }

        if (
          (scenario.type === ScenarioType.EMAIL_PAGE_GO_MOBILE ||
            scenario.type === ScenarioType.EMAIL_PAGE_GO_INTERNET) &&
          answers &&
          answers['confirm-alert'] &&
          answers['confirm-alert'][0]
        ) {
          yield call(sendAlert, answers)
        }
      }
      yield put(finishScenario())
    }
  } else if (nextStepId === Steps.VISIT_ESIM) {
    clearScenarioCookie()
    setCookie('visitor', '2', 7)
    const locale = getLocale()
    if (locale === 'fr') {
      window.location.assign('https://www.planhub.ca/fr/voyage/esim-canada')
    } else {
      window.location.assign('https://www.planhub.ca/travel/esim-canada')
    }
  } else if (nextStepId === Steps.START_QUESTIONNAIRE) {
    setCookie('visitor', '3', 7)
    const cei = getCodedUrlParameter()
    const link = getQueryParams().link as string
    const scenarioCookie = yield select(selectScenarioCookie)

    if (scenarioCookie) {
      setCookie('scenario', JSON.stringify(scenarioCookie), 1)
    } else {
      setCookie('scenario', JSON.stringify({}), 1)
    }

    const referrer: string = encodeURIComponent(yield select(selectReferrerUrl))
    const { protocol, host, pathname } = window.location
    let scenarioType = yield select(selectScenarioScenarioType)
    if (!scenarioType) {
      scenarioType = 'decision'
    }
    const locale = getLocale()
    const additionalQueryParameters = getAdditionalQueryParameters()
    const parameterI = additionalQueryParameters.i as string
    const parameterP = additionalQueryParameters.p as string
    const parameterL = additionalQueryParameters.l as string
    const parameterC = additionalQueryParameters.c as string

    // clearScenarioCookie()
    window.location.assign(
      `${protocol}//${host}${pathname}?locale=${locale}&scenarioType=${scenarioType}${
        cei ? '&cei=' + cei : ''
      }&referrer=${referrer}&link=${link}&i=${parameterI}&p=${parameterP}&l=${encodeURIComponent(
        parameterL,
      )}&c=${parameterC}&dyn=1`,
    )
  } else if (nextStepId) {
    const prefilledAnswers: Answers = yield select(selectPrefilledAnswers)
    const isCurStepPrefilled: boolean = yield select(selectIsCurrentStepPrefilled)

    yield put(nextStep({ id: nextStepId, disablePersistence: isCurStepPrefilled } as NextStepPayload))

    if (prefilledAnswers[nextStepId]) {
      yield call(answerAdded, addAnswer({ stepId: nextStepId, answerIds: prefilledAnswers[nextStepId] as string[] }))
    }
  }

  if (nextStepId === Steps.BUY_OR_BRING_PHONE) {
    if (scenario.type === ScenarioType.MOBILE_ALERT) {
      const answers: Answers = yield select(selectAnswers)

      if (answers && answers['phone-model-id'] && answers['phone-model-id'][0] !== '2') {
        yield put(gotoStep({ id: Steps.EMAIL_ADDRESS }))
      }
    }
  }

  if (nextStepId === Steps.BIG_ENTREPRISE2) {
    const province = getCookie('province')

    if (province !== 'quebec') {
      yield put(
        addAnswer({
          stepId: Steps.BIG_ENTREPRISE2,
          answerIds: ['not-set'],
        }),
      )
    }
  }
}

function getCodedUrlParameter() {
  return getQueryParams().cei as string
}

function findAlertScenarioType(alertType: string, scenarioType: ScenarioType): ScenarioType {
  if (alertType === 'price-drop') {
    if (scenarioType === ScenarioType.INTERNET_ALERT) {
      return ScenarioType.INTERNET_ALERT_OBSERVER
    } else if (scenarioType === ScenarioType.MOBILE_ALERT) {
      return ScenarioType.MOBILE_ALERT_OBSERVER
    }
  }

  if (alertType === 'alert-search') {
    if (scenarioType === ScenarioType.INTERNET_ALERT_OBSERVER) {
      return ScenarioType.INTERNET_ALERT
    } else if (scenarioType === ScenarioType.MOBILE_ALERT_OBSERVER) {
      return ScenarioType.MOBILE_ALERT
    }
  }

  return scenarioType
}

function* sendReviews(answers: Answers) {
  const scenarioType: ScenarioType = yield select(selectScenarioType)
  sendReviewsRequest(answers, scenarioType)
}

function* sendAlert(answers: Answers) {
  const scenarioType: ScenarioType = yield select(selectScenarioType)
  sendAlertRequest(answers, scenarioType)
}

function* sendLead(answers: Answers) {
  const scenarioType: ScenarioType = yield select(selectScenarioType)
  sendLeadRequest(answers, scenarioType)
}

function* sendNewsletter(answers: Answers) {
  // delete code by dec 2024
  console.log(answers)
  // const scenarioType: ScenarioType = yield select(selectScenarioType)
  // sendNewsletterRequest(answers, scenarioType)
}

function* sendNewTicket(answers: Answers) {
  const scenarioType: ScenarioType = yield select(selectScenarioType)
  sendNewTicketRequest(answers, scenarioType)
}

function* sendNewImmigrantInfo(answers: Answers) {
  const scenarioType: ScenarioType = yield select(selectScenarioType)
  sendNewImmigrantRequest(answers, scenarioType)
}

function* sendB2BInfo(answers: Answers) {
  const scenarioType: ScenarioType = yield select(selectScenarioType)
  sendB2BRequest(answers, scenarioType)
}

function* sendConsentNewsletterInfo(answers: Answers) {
  const scenarioType: ScenarioType = yield select(selectScenarioType)
  sendConsentNewsletterRequest(answers, scenarioType)
}

function* sendNewUserUpdatedInfo(answers: Answers) {
  const scenarioType: ScenarioType = yield select(selectScenarioType)
  sendNewUserUpdatedRequest(answers, scenarioType)
}
